import { useEffect, useRef, useState } from 'react';
import type { MemoryStats } from '../../data/types';
import { getSources } from '../../stores/sources';

export type StatsState =
  | { phase: 'unconfigured' }
  | { phase: 'pending' }
  | { phase: 'ready'; stats: MemoryStats }
  | { phase: 'error'; message: string };

/** useMemoryStats fetches one tenant's counts. Not a store: one object per
 *  page, no deltas, no paging — a request generation guards against a slow
 *  answer landing on a later tenant. `unconfigured` when the source has no
 *  admin route, which reads differently from zero. */
export function useMemoryStats(estateId: string, tenantId: string, tick = 0): StatsState {
  const [state, setState] = useState<StatsState>({ phase: 'pending' });
  const gen = useRef(0);
  useEffect(() => {
    const mine = ++gen.current;
    const src = getSources().live;
    if (src.kind === 'empty') {
      setState({ phase: 'unconfigured' });
      return;
    }
    setState((s) => (s.phase === 'ready' ? s : { phase: 'pending' }));
    src.memoryStats(estateId, tenantId).then(
      (stats) => {
        if (mine === gen.current) setState({ phase: 'ready', stats });
      },
      (err: unknown) => {
        if (mine === gen.current) {
          setState({ phase: 'error', message: err instanceof Error ? err.message : String(err) });
        }
      },
    );
  }, [estateId, tenantId, tick]);
  return state;
}
